import { theme } from "../../../../../shared/frontend/ui";
import React from "react";
import { applyDocumentSheetLayout } from "./documentSheetOverflow";

type DocumentPreviewSurface = "email" | "letter" | "table";

type Props = {
  /** Vom Server erzeugtes Dokument; die Komponente baut seinen Inhalt nicht nach. */
  html: string;
  /** Briefpapier bleibt weiß, die E-Mail-Arbeitsfläche folgt dem Anwendungstheme. */
  surface: DocumentPreviewSurface;
  title: string;
  style?: React.CSSProperties;
  className?: string;
  /** Lässt das Dokument mit seinem Inhalt wachsen; der Browser behält den einzigen Scrollbalken. */
  autoHeight?: boolean;
  scrolling?: "auto" | "no";
  /**
   * Ergänzt die editor-spezifische Bedienung, nachdem die gemeinsame Fläche gesetzt wurde.
   * Ein optionaler Rückgabewert räumt Ereignis-Handler der vorherigen Vorschau auf, bevor neues
   * HTML in denselben iframe geladen wird.
   */
  onDocumentReady?: (document: Document) => void | (() => void);
};

/**
 * Gemeinsame isolierte Vorschaufläche für Briefe und E-Mails.
 *
 * Ein iframe erbt die Ant-Theme-Variablen nicht. Diese Komponente überträgt deshalb die
 * Arbeitsfläche in sein eigenes Dokument, damit Erscheinungsbild und Textvorlagen dieselbe
 * Darstellung verwenden. Der Dokumentinhalt selbst bleibt vollständig serverseitig gerendert.
 */
export const DocumentPreviewFrame = React.forwardRef<HTMLIFrameElement, Props>(function DocumentPreviewFrame(
  { html, surface, title, style, className, autoHeight = false, scrolling = "auto", onDocumentReady },
  forwardedRef,
) {
  const { token } = theme.useToken();
  const frameRef = React.useRef<HTMLIFrameElement | null>(null);
  const documentCleanupRef = React.useRef<(() => void) | undefined>(undefined);
  const editorCleanupRef = React.useRef<(() => void) | undefined>(undefined);
  /**
   * Zählt die geladenen Dokumente. Die Bedienung wird daran **und** an `onDocumentReady` neu
   * eingerichtet: Ändert der Editor sein Verhalten — etwa beim Wechsel zwischen Gestaltung und
   * Text —, muss sie neu greifen. Am `onLoad` allein hing sie nur am HTML und blieb beim
   * Moduswechsel auf dem alten Stand.
   */
  const [loadCount, setLoadCount] = React.useState(0);

  const setFrameRef = React.useCallback(
    (node: HTMLIFrameElement | null) => {
      frameRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const applySurface = React.useCallback(
    (document?: Document | null) => {
      const previewDocument = document ?? frameRef.current?.contentDocument;
      if (!previewDocument) return;

      let surfaceStyle = previewDocument.getElementById("mq-document-preview-surface") as HTMLStyleElement | null;
      if (!surfaceStyle) {
        surfaceStyle = previewDocument.createElement("style");
        surfaceStyle.id = "mq-document-preview-surface";
        previewDocument.head?.appendChild(surfaceStyle);
      }

      surfaceStyle.textContent = `
        html { background: ${token.colorBgLayout} !important; }
        ${surface === "letter" ? "body { background: transparent !important; }" : ""}
        ${surface === "table" ? `html { background: ${token.colorBgLayout} !important; }` : ""}
      `;
    },
    [surface, token.colorBgLayout],
  );

  // Theme- oder Flächenwechsel können stattfinden, ohne dass srcDoc neu geladen wird.
  React.useEffect(() => applySurface(), [applySurface]);

  React.useEffect(() => () => documentCleanupRef.current?.(), []);

  const observeHeight = React.useCallback((previewDocument: Document) => {
    if (!autoHeight) return undefined;
    const frame = frameRef.current;
    if (!frame) return undefined;

    let animationFrame = 0;
    const update = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        // Vor der Höhenmessung: Ein bedingtes Folgeseitenblatt muss am gesetzten Dokument
        // entschieden werden; seine Sichtbarkeit kann die Höhe der Fläche ändern.
        applyDocumentSheetLayout(previewDocument);
        // Für E-Mail misst data-mq-email-page die exakte Tabellenhöhe der Mail inklusive aller Abstände.
        // Für Brief misst mq-sheet-container zuzüglich body-Padding (oben/unten) oder der Body-Inhalt.
        // documentElement.scrollHeight wird bewusst nicht abgefragt: Sobald die Frame-Höhe einmal groß war,
        // bliebe documentElement.scrollHeight auf dieser Höhe stehen und könnte nie wieder schrumpfen.
        const emailPage = previewDocument.querySelector<HTMLElement>("[data-mq-email-page]");
        const sheetContainer = previewDocument.querySelector<HTMLElement>(".mq-sheet-container");
        const body = previewDocument.body;
        let targetHeight = 0;
        if (emailPage) {
          targetHeight = emailPage.scrollHeight;
        } else if (sheetContainer && body) {
          const bodyStyle = previewDocument.defaultView?.getComputedStyle(body);
          const paddingTop = parseFloat(bodyStyle?.paddingTop || "0") || 0;
          const paddingBottom = parseFloat(bodyStyle?.paddingBottom || "0") || 0;
          targetHeight = Math.max(
            sheetContainer.scrollHeight + paddingTop + paddingBottom,
            sheetContainer.offsetHeight + paddingTop + paddingBottom,
            body.scrollHeight,
            body.offsetHeight
          );
        } else if (body) {
          targetHeight = Math.max(body.scrollHeight, body.offsetHeight);
          if (surface === "table") {
            // Die Tabelle nutzt ein serverseitig gerendertes Einzelblatt. Dessen äußere Ränder
            // gehören zur iframe-Höhe, sonst würde der untere Blattrand abgeschnitten.
            const bodyStyle = previewDocument.defaultView?.getComputedStyle(body);
            const marginTop = parseFloat(bodyStyle?.marginTop || "0") || 0;
            const marginBottom = parseFloat(bodyStyle?.marginBottom || "0") || 0;
            targetHeight += marginTop + marginBottom;
          }
        }
        if (targetHeight > 0) {
          frame.style.height = `${Math.ceil(targetHeight)}px`;
        }
      });
    };
    update();

    const resizeObserver = new ResizeObserver(update);
    if (previewDocument.body) resizeObserver.observe(previewDocument.body);
    if (previewDocument.documentElement) resizeObserver.observe(previewDocument.documentElement);
    const emailPage = previewDocument.querySelector("[data-mq-email-page]");
    if (emailPage) resizeObserver.observe(emailPage);
    const sheetContainer = previewDocument.querySelector(".mq-sheet-container");
    if (sheetContainer) resizeObserver.observe(sheetContainer);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(previewDocument.body ?? previewDocument.documentElement, {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [autoHeight, surface]);

  React.useEffect(() => {
    const previewDocument = frameRef.current?.contentDocument;
    if (loadCount === 0 || !previewDocument) return;

    const cleanup = onDocumentReady?.(previewDocument);
    editorCleanupRef.current = typeof cleanup === "function" ? cleanup : undefined;
    return () => {
      editorCleanupRef.current?.();
      editorCleanupRef.current = undefined;
    };
  }, [loadCount, onDocumentReady]);

  return (
    <iframe
      ref={setFrameRef}
      title={title}
      srcDoc={html}
      // Das Markup kommt vom Server; im Dokument wird kein Script ausgeführt. Die aufrufende
      // Editor-Komponente ergänzt ihre Bedienung nach dem Laden von außen.
      sandbox="allow-same-origin"
      className={className}
      style={style}
      scrolling={scrolling}
      onLoad={() => {
        const previewDocument = frameRef.current?.contentDocument;
        if (!previewDocument) return;
        documentCleanupRef.current?.();
        applySurface(previewDocument);
        // Auch ohne mitwachsende Höhe: Ein bedingtes Folgeseitenblatt muss nach dem ersten Satz
        // einmal berechnet werden.
        applyDocumentSheetLayout(previewDocument);
        const heightCleanup = observeHeight(previewDocument);
        documentCleanupRef.current = () => heightCleanup?.();
        setLoadCount((current) => current + 1);
      }}
    />
  );
});
